import { ApiError, ApiErrorCode } from '../types/apiError'

/**
 * Ponto único de leitura de erro da API.
 *
 * Precedência da mensagem — cobre o envelope atual do middleware e todo o
 * formato legado que ainda pode estar em trânsito durante a migração:
 *
 *   1. body.error.message   envelope atual
 *   2. body.error.error     espelho depreciado do envelope / formato antigo do reCAPTCHA
 *   3. body.error           string solta (formato antigo de "Origem não informada.")
 *   4. body.message         corpo cru (200 com retorno:false, controllers de checkout)
 *   5. body.mensagem        idem, grafia em português
 *   6. body.erro            resposta que não passou pelo middleware
 *   7. fallback por status  tabela abaixo
 *
 * A mensagem técnica do axios ("Request failed with status code 422") NUNCA é
 * usada: ela vazava para a UI antes desta normalização.
 */

const MENSAGEM_POR_STATUS: Record<number, string> = {
  400: 'Requisição inválida.',
  401: 'Não autenticado.',
  402: 'Pagamento recusado. Verifique os dados do cartão.',
  403: 'Acesso negado.',
  404: 'Recurso não encontrado.',
  405: 'Operação não permitida.',
  409: 'Esta operação não está mais disponível.',
  422: 'Dados informados estão inválidos ou incorretos.',
  429: 'Muitas tentativas. Aguarde alguns instantes.',
  500: 'Erro interno no servidor.',
  502: 'Serviço temporariamente indisponível. Tente novamente.',
  503: 'Serviço temporariamente indisponível. Tente novamente.'
}

const MENSAGEM_GENERICA = 'Não foi possível processar a requisição.'
const MENSAGEM_SEM_CONEXAO = 'Falha de conexão. Verifique sua internet e tente novamente.'

type CorpoDeErro = {
  error?: unknown
  message?: unknown
  mensagem?: unknown
  erro?: unknown
}

function texto(valor: unknown): string | undefined {
  return typeof valor === 'string' && valor.trim() ? valor.trim() : undefined
}

function lerHeader(headers: unknown, nome: string): string | undefined {
  if (!headers || typeof headers !== 'object') return undefined

  const alvo = nome.toLowerCase()

  for (const [chave, valor] of Object.entries(headers as Record<string, unknown>)) {
    if (chave.toLowerCase() === alvo) {
      return texto(Array.isArray(valor) ? valor[0] : valor)
    }
  }

  return undefined
}

function extrairMensagem(body: unknown): string | undefined {
  if (typeof body === 'string') {
    const bruto = body.trim()
    // Página de erro HTML de proxy não é mensagem de usuário.
    return bruto && !bruto.startsWith('<') && bruto.length <= 300 ? bruto : undefined
  }

  if (!body || typeof body !== 'object') return undefined

  const { error, message, mensagem, erro } = body as CorpoDeErro

  if (error && typeof error === 'object') {
    const envelope = error as { message?: unknown; error?: unknown }
    const doEnvelope = texto(envelope.message) ?? texto(envelope.error)
    if (doEnvelope) return doEnvelope
  }

  return texto(error) ?? texto(message) ?? texto(mensagem) ?? texto(erro)
}

function extrairCampos(body: unknown): Record<string, string[]> | undefined {
  if (!body || typeof body !== 'object') return undefined

  const { error } = body as CorpoDeErro
  if (!error || typeof error !== 'object') return undefined

  const campos = (error as { errors?: unknown }).errors
  if (!campos || typeof campos !== 'object' || Array.isArray(campos)) return undefined

  const normalizado: Record<string, string[]> = {}

  for (const [campo, valor] of Object.entries(campos as Record<string, unknown>)) {
    const mensagens = (Array.isArray(valor) ? valor : [valor])
      .map((item) => texto(item))
      .filter((item): item is string => item !== undefined)

    if (mensagens.length) normalizado[campo] = mensagens
  }

  return Object.keys(normalizado).length ? normalizado : undefined
}

function extrairCode(body: unknown): ApiErrorCode | undefined {
  if (!body || typeof body !== 'object') return undefined

  const { error } = body as CorpoDeErro
  if (!error || typeof error !== 'object') return undefined

  return texto((error as { code?: unknown }).code) as ApiErrorCode | undefined
}

function extrairRetryAfter(headers: unknown): number | undefined {
  const bruto = lerHeader(headers, 'retry-after')
  if (bruto === undefined) return undefined

  // O header também aceita HTTP-date; só tratamos o formato em segundos.
  const segundos = Number(bruto)
  return Number.isFinite(segundos) && segundos >= 0 ? segundos : undefined
}

/**
 * Normaliza uma resposta HTTP de falha já obtida — usado pelos endpoints com
 * `validateStatus: () => true`, que não lançam.
 */
export function normalizeHttpFailure(
  status: number,
  body: unknown,
  headers?: unknown
): ApiError {
  const daApi = extrairMensagem(body)
  const usedFallback = daApi === undefined

  return new ApiError({
    status,
    message: daApi ?? MENSAGEM_POR_STATUS[status] ?? MENSAGEM_GENERICA,
    code: extrairCode(body),
    fields: extrairCampos(body),
    retryAfter: extrairRetryAfter(headers),
    requestId: lerHeader(headers, 'x-request-id'),
    usedFallback,
    raw: body
  })
}

/**
 * Normaliza o que o axios lançou. Sem `response`, o navegador não chegou ao
 * middleware — status 0 e mensagem de conexão, nunca a string técnica.
 */
export function normalizeApiError(erro: unknown): ApiError {
  if (erro instanceof ApiError) return erro

  const resposta = (erro as { response?: { status?: number; data?: unknown; headers?: unknown } })
    ?.response

  if (!resposta || typeof resposta.status !== 'number') {
    return new ApiError({
      status: 0,
      message: MENSAGEM_SEM_CONEXAO,
      code: 'NETWORK',
      usedFallback: true,
      raw: erro
    })
  }

  return normalizeHttpFailure(resposta.status, resposta.data, resposta.headers)
}

/**
 * HTTP 200 com `retorno:false`: a API respondeu, mas a regra de negócio
 * recusou. Só é aplicado a endpoints que EXECUTAM uma operação — em
 * validadores, `retorno:false` é a resposta correta, não um erro.
 */
export function assertBusinessOk<T>(body: T, contexto: string): T {
  const corpo = body as { retorno?: unknown } | null | undefined

  if (corpo && typeof corpo === 'object' && corpo.retorno === false) {
    throw new ApiError({
      status: 200,
      message: extrairMensagem(corpo) ?? `Não foi possível concluir: ${contexto}.`,
      code: 'BUSINESS_RULE',
      usedFallback: extrairMensagem(corpo) === undefined,
      raw: corpo
    })
  }

  return body
}
